import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { subscriptionService, type UserProfile } from '../services/subscriptionService';
import { toast } from 'react-hot-toast';

export function useSubscription() {
  const { user } = useAuthStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Load user profile in response to global subscription updates (e.g., after Stripe return).
  useEffect(() => {
    const handleSubscriptionUpdated = () => {
      if (!user?.id) return;
      loadProfile();
    };
    window.addEventListener('subscription-updated', handleSubscriptionUpdated);
    return () => window.removeEventListener('subscription-updated', handleSubscriptionUpdated);
  }, [user?.id]);

  // Load user profile
  useEffect(() => {
    if (user?.id) {
      loadProfile();
    } else {
      setProfile(null);
      setLoading(false);
    }
  }, [user?.id]);

  const loadProfile = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const userProfile = await subscriptionService.getUserProfile(user.id);
      setProfile(userProfile);
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToPro = async (interval: 'monthly' | 'annual' = 'monthly') => {
    if (!user?.id) {
      toast.error('Please log in to subscribe');
      return;
    }

    setIsSubscribing(true);
    try {
      const success = await subscriptionService.subscribeToPro(user.id, interval);
      if (!success) {
        toast.error('Failed to start subscription process');
      }
    } catch (error) {
      console.error('Error subscribing to Pro:', error);
      toast.error('Failed to start subscription process');
    } finally {
      setIsSubscribing(false);
    }
  };

  const openBillingPortal = async () => {
    if (!user?.id) {
      toast.error('Please log in to manage subscription');
      return;
    }

    try {
      const success = await subscriptionService.openBillingPortal(user.id);
      if (!success) {
        toast.error('Failed to open billing portal');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Failed to open billing portal');
    }
  };

  const cancelSubscription = async () => {
    if (!user?.id) {
      toast.error('Please log in to cancel subscription');
      return;
    }
    setIsCancelling(true);
    try {
      const result = await subscriptionService.cancelSubscription(user.id);
      if (result.success) {
        toast.success('Subscription will cancel at the end of your billing period. You’ll keep Pro until then.');
        await loadProfile();
      } else {
        toast.error(result.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  const isProUser = subscriptionService.isProUser(profile);
  const isSubscriptionActive = subscriptionService.isSubscriptionActive(profile);

  return {
    profile,
    loading,
    isSubscribing,
    isCancelling,
    isProUser,
    isSubscriptionActive,
    subscribeToPro,
    openBillingPortal,
    cancelSubscription,
    loadProfile,
  };
} 